import { useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Skeleton,
  SkeletonItem,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Briefcase20Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
  Info20Regular,
  LockClosed12Regular,
  People20Regular,
  PeopleLock20Regular,
  Search20Regular,
  // 12 px pour s'aligner sur LockClosed12Regular du badge « Restreint » — il
  // n'existe pas de PlugDisconnected en 12.
  Warning12Regular,
} from '@fluentui/react-icons';
import { useAdminSpaces } from '@/hooks/useAdmin';
import { humanFileSize } from '@/utils/fileSize';
import { ContainerVisibilityDialog } from '@/features/visibility/ContainerVisibilityDialog';
import type { AdminSpaceRow, AdminSpaceSort } from '@/services/api/endpoints';

// Page volontairement courte : le panneau est étroit et chaque ligne fait deux
// lignes de texte. La navigation se fait par pages plutôt que par un « voir
// plus » qui empilerait 151 espaces dans un conteneur défilant.
const PAGE_SIZE = 25;

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0 },
  hintBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  hintBannerIcon: { flexShrink: 0, marginTop: '2px', color: tokens.colorBrandForeground1 },
  filters: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  summary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  // Pas rouge : l'absence de groupe est une tâche à faire, pas une panne.
  warn: { color: tokens.colorPaletteDarkOrangeForeground1, fontWeight: tokens.fontWeightSemibold },
  spaceCell: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  spaceIcon: { flexShrink: 0, marginTop: '2px', color: tokens.colorNeutralForeground3 },
  spaceTexts: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  metrics: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  badges: { display: 'flex', gap: tokens.spacingHorizontalXXS, flexWrap: 'wrap' },
  visibilityCell: { width: '100px' },
  visibilityStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1px',
    minWidth: 0,
  },
  actionCell: { width: '44px', textAlign: 'right', paddingRight: 0 },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  empty: {
    padding: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

/** « 0 o » plutôt que rien : dans un bilan de stockage, le vide est une information. */
function bytesLabel(bytes: number): string {
  return humanFileSize(bytes) || '0 o';
}

/**
 * Inventaire de tous les espaces, réservé aux administrateurs.
 *
 * SEUL ÉCRAN QUI MONTRE TOUT : l'arbre filtre par droits et masque les archivés,
 * la recherche aussi. C'est donc ici — et nulle part ailleurs — qu'on peut
 * reprendre la main sur un espace devenu invisible parce que quelqu'un l'a
 * restreint sans s'y inclure. Ce que cela suppose côté serveur est déjà en place :
 * les administrateurs voient tous les espaces (migration 0020).
 *
 * Recherche, tri et pagination sont TOUS calculés en base : trier côté client
 * supposerait d'avoir déjà toutes les lignes, ce que la pagination interdit.
 */
export function SpacesOverviewPanel() {
  const styles = useStyles();
  const [q, setQ] = useState('');
  const [debouncedQ] = useDebounce(q.trim(), 300);
  const [sort, setSort] = useState<AdminSpaceSort>('name');
  const [restrictedOnly, setRestrictedOnly] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(true);
  // 0029 : les espaces sans groupe Microsoft 365 sont invisibles pour les
  // utilisateurs. Cette bascule est la liste de travail de la correction.
  const [missingGroupOnly, setMissingGroupOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [target, setTarget] = useState<AdminSpaceRow | null>(null);

  // Tout changement de filtre ramène à la première page : rester à l'offset 200
  // après une recherche qui ne rend que 3 lignes afficherait une page vide.
  const resetPage = () => setOffset(0);

  const { data, isPending, error, isFetching } = useAdminSpaces({
    q: debouncedQ,
    sort,
    restrictedOnly,
    includeArchived,
    // `undefined` et non `false` : la case décochée ne demande AUCUN filtre, alors
    // que `false` demanderait « seulement ceux qui ont un groupe ».
    missingGroup: missingGroupOnly ? true : undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totals = data?.totals;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className={styles.wrap}>
      <div className={styles.hintBanner}>
        <Info20Regular className={styles.hintBannerIcon} />
        <span>
          Tous les espaces de l'add-in, projets et espaces de service, y compris ceux dont la
          visibilité est restreinte et ceux qui sont archivés. Réservé aux administrateurs.
        </span>
      </div>

      <div className={styles.filters}>
        <Input
          value={q}
          placeholder="Nom ou numéro de projet…"
          contentBefore={<Search20Regular />}
          onChange={(_e, d) => {
            setQ(d.value);
            resetPage();
          }}
        />
        <div className={styles.filterRow}>
          <Select
            value={sort}
            aria-label="Trier les espaces"
            onChange={(_e, d) => {
              setSort(d.value as AdminSpaceSort);
              resetPage();
            }}
          >
            <option value="name">Trier par nom</option>
            <option value="size">Trier par volume</option>
            <option value="mails">Trier par nombre de mails</option>
          </Select>
        </div>
        <div className={styles.filterRow}>
          <Checkbox
            label="Restreints uniquement"
            checked={restrictedOnly}
            onChange={(_e, d) => {
              setRestrictedOnly(d.checked === true);
              resetPage();
            }}
          />
          <Checkbox
            label="Inclure les archivés"
            checked={includeArchived}
            onChange={(_e, d) => {
              setIncludeArchived(d.checked === true);
              resetPage();
            }}
          />
          <Checkbox
            label="Sans groupe 365"
            title="Espaces qu'aucun utilisateur ne voit : pas de groupe Microsoft 365, donc pas de bibliothèque où ranger les mails"
            checked={missingGroupOnly}
            onChange={(_e, d) => {
              setMissingGroupOnly(d.checked === true);
              resetPage();
            }}
          />
        </div>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>Inventaire indisponible : {error.message}</MessageBarBody>
        </MessageBar>
      )}

      {/* Cumuls sur tout le périmètre filtré, jamais sur la page affichée. */}
      {totals && (
        <Text className={styles.summary}>
          {totals.containers} espace{totals.containers > 1 ? 's' : ''} · {totals.restricted} restreint
          {totals.restricted > 1 ? 's' : ''} · {totals.folders} dossier
          {totals.folders > 1 ? 's' : ''} · {totals.mails} mail{totals.mails > 1 ? 's' : ''} ·{' '}
          {bytesLabel(totals.bytes)}
          {/* L'écart avec Microsoft 365, mis en avant : ces espaces n'apparaissent
              ni dans l'arbre, ni dans la recherche de personne, et le rangement y
              est refusé. C'est le seul endroit du produit où le chiffre existe. */}
          {totals.withoutGroup > 0 && (
            <>
              {' · '}
              <span className={styles.warn}>
                {totals.withoutGroup} sans groupe 365
              </span>
            </>
          )}
          {/* Les mails en corbeille occupent ENCORE le stockage : les taire
              donnerait un bilan qui ne correspond pas à la facture S3. */}
          {totals.trashedMails > 0 && (
            <>
              {' '}
              <span className={styles.hint}>
                (+ {totals.trashedMails} en corbeille, {bytesLabel(totals.trashedBytes)} pas encore
                purgés)
              </span>
            </>
          )}
        </Text>
      )}

      {isPending ? (
        <Skeleton>
          <SkeletonItem size={24} />
          <SkeletonItem size={24} style={{ marginTop: 8 }} />
          <SkeletonItem size={24} style={{ marginTop: 8 }} />
        </Skeleton>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          {missingGroupOnly && !debouncedQ && !restrictedOnly
            ? 'Tous les espaces ont un groupe Microsoft 365 : aucun écart à corriger.'
            : debouncedQ || restrictedOnly || !includeArchived || missingGroupOnly
              ? 'Aucun espace ne correspond à ces critères.'
              : "Aucun espace pour l'instant."}
        </div>
      ) : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Espace</TableHeaderCell>
              <TableHeaderCell className={styles.visibilityCell}>Visibilité</TableHeaderCell>
              <TableHeaderCell className={styles.actionCell} aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => {
              const Icon = s.kind === 'project' ? Briefcase20Regular : People20Regular;
              return (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className={styles.spaceCell}>
                      <Icon className={styles.spaceIcon} />
                      <div className={styles.spaceTexts}>
                        <Text size={300} className={styles.name} title={s.name}>
                          {s.name}
                        </Text>
                        <Text className={styles.metrics}>
                          {s.folderCount} dossier{s.folderCount > 1 ? 's' : ''} · {s.mailCount} mail
                          {s.mailCount > 1 ? 's' : ''} · {bytesLabel(s.bytes)}
                        </Text>
                        {(s.archived || !s.hasGroup) && (
                          <div className={styles.badges}>
                            {s.archived && (
                              <Badge appearance="outline" color="subtle" size="small">
                                Archivé
                              </Badge>
                            )}
                            {/* 0029 : la seule surface du produit qui le dit. Sans
                                ce badge, un administrateur n'a aucun moyen de
                                savoir pourquoi un espace a disparu de l'arbre. */}
                            {!s.hasGroup && (
                              <Badge
                                appearance="outline"
                                color="warning"
                                size="small"
                                icon={<Warning12Regular />}
                                title="Aucun groupe Microsoft 365 : cet espace est invisible pour les utilisateurs et le rangement y est refusé"
                              >
                                Sans groupe 365
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={styles.visibilityCell}>
                    {s.isRestricted ? (
                      // Le mot AVANT le chiffre : un « 12 » seul dans une colonne
                      // « Visibilité » n'apprend rien. L'effectif ne veut d'ailleurs
                      // dire quelque chose QUE sur un espace restreint — sur un
                      // espace public, tout le monde a accès quels que soient les
                      // droits enregistrés.
                      <div
                        className={styles.visibilityStack}
                        title={
                          `${s.peopleCount} personne${s.peopleCount > 1 ? 's' : ''} y ont accès : ` +
                          `${s.userCount} nommée${s.userCount > 1 ? 's' : ''} et ${s.groupCount} groupe${s.groupCount > 1 ? 's' : ''}.` +
                          " Les administrateurs de l'add-in y gardent accès."
                        }
                      >
                        <Badge
                          appearance="outline"
                          color="subtle"
                          size="small"
                          icon={<LockClosed12Regular />}
                        >
                          Restreint
                        </Badge>
                        <Text className={styles.hint}>
                          {s.peopleCount} personne{s.peopleCount > 1 ? 's' : ''}
                        </Text>
                      </div>
                    ) : (
                      <Text className={styles.hint} title="Visible par tout le monde">
                        Public
                      </Text>
                    )}
                  </TableCell>
                  <TableCell className={styles.actionCell}>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<PeopleLock20Regular />}
                      title={`Gérer la visibilité de « ${s.name} »`}
                      aria-label={`Gérer la visibilité de ${s.name}`}
                      onClick={() => setTarget(s)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {total > PAGE_SIZE && (
        <div className={styles.pager}>
          <Text className={styles.hint}>
            {from}–{to} sur {total}
            {isFetching ? ' · actualisation…' : ''}
          </Text>
          <div>
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronLeft16Regular />}
              disabled={offset === 0}
              aria-label="Page précédente"
              title="Page précédente"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronRight16Regular />}
              disabled={to >= total}
              aria-label="Page suivante"
              title="Page suivante"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            />
          </div>
        </div>
      )}

      {/* UNE SEULE instance du dialogue, pas une par ligne : monter 25 dialogues
          fermés ferait autant de lectures de droits à l'ouverture du panneau. */}
      {target && (
        <ContainerVisibilityDialog
          container={{ id: target.id, name: target.name }}
          open
          onOpenChange={(o) => {
            if (!o) setTarget(null);
          }}
        />
      )}
    </div>
  );
}
